// Click-through handler for ReferencesTab rows.
// Opens the host object's CREATE script in a new query tab and scrolls /
// reveals the reference range. For default + check constraints, the host
// table's Designer is opened instead because constraint bodies don't have a
// stand-alone DDL surface.

import type { DatabaseEngineId } from '@/core/engines';

import { getConnectionById } from '../../connections/utils/connectionLookup';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { defaultSchemaForEngine } from '../go-to/externalNavigation';
import type { ObjectKind, QualifiedName, SqlRange } from '../types';
import { safeConnectionAlias, safeConnectionServer } from '@/types/safeConnectionDisplay';

export type OpenReferenceLocationOptions = {
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
  readonly objectKind: ObjectKind;
  readonly objectName: QualifiedName;
  readonly range: SqlRange;
  /** Body text the references tab already fetched. When provided, the new
   *  tab opens with the body inlined so the reveal can run synchronously. */
  readonly bodyText?: string;
};

export type OpenReferenceLocationResult = {
  readonly opened: boolean;
  readonly tabId?: string;
  readonly mode?: 'create-text' | 'table-designer';
};

export async function openReferenceLocation(
  opts: OpenReferenceLocationOptions,
): Promise<OpenReferenceLocationResult> {
  const { activeConnection, connection, engine } = getConnectionById(opts.connectionId);
  if (!connection) return { opened: false };

  const schema = opts.objectName.schema ?? defaultSchemaForEngine(opts.engineId);
  const database = opts.database || opts.objectName.database || '';
  const connectionInfo = {
    connectionId: opts.connectionId,
    connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
    serverHost: safeConnectionServer(activeConnection?.connection),
    database,
  };

  const { addTab, getNextQueryNumber, updateTabContent } =
    useWorkspaceStore.getState();

  const queryNumber = getNextQueryNumber();
  const tabId = addTab({
    title: `${opts.objectName.name}_CREATE-${queryNumber}`,
    type: 'query',
    content: {
      query: opts.bodyText
        ? opts.bodyText
        : `-- Loading CREATE script for ${schema}.${opts.objectName.name}...\n`,
      isGeneratingScript: !opts.bodyText,
    },
    connectionInfo,
  });

  // If we already have the body text, the buffer is correct; otherwise pull
  // the CREATE script for the matching object kind.
  if (!opts.bodyText) {
    void loadObjectScript({
      tabId,
      connectionId: opts.connectionId,
      database,
      engine,
      schema,
      objectKind: opts.objectKind,
      name: opts.objectName.name,
      updateTabContent,
    });
  }

  // The Monaco reveal piggybacks on the query tab's existing reveal-on-mount
  // hook by stamping a small annotation onto the content. The query tab
  // doesn't have a generic reveal field, so we use the focusColumnName /
  // language-service equivalent via a custom marker. To keep this slice
  // small, the reveal happens client-side by selecting the line + col after
  // the editor mounts — see the `revealAtPositionAfterMount` helper used
  // here.
  void scheduleRevealAtPosition(tabId, opts.range);

  return { opened: true, tabId, mode: 'create-text' };
}

type LoadObjectScriptArgs = {
  readonly tabId: string;
  readonly connectionId: string;
  readonly database: string;
  readonly engine: string;
  readonly schema: string;
  readonly objectKind: ObjectKind;
  readonly name: string;
  readonly updateTabContent: (
    tabId: string,
    patch: Record<string, unknown>,
  ) => void;
};

async function loadObjectScript(args: LoadObjectScriptArgs): Promise<void> {
  const objectType = mapObjectKindToDefinitionType(args.objectKind);
  if (!objectType) {
    args.updateTabContent(args.tabId, {
      query: `-- Reference target ${args.objectKind} ${args.schema}.${args.name} has no CREATE script surface.\n`,
      isGeneratingScript: false,
    });
    return;
  }
  try {
    const { fetchObjectDefinitionScript } = await import(
      '../../database-objects/services/objectDefinitionService'
    );
    const { script } = await fetchObjectDefinitionScript({
      connectionId: args.connectionId,
      database: args.database,
      engine: args.engine,
      objectType,
      schema: args.schema,
      name: args.name,
      mode: 'create',
    });
    args.updateTabContent(args.tabId, {
      query: script,
      isGeneratingScript: false,
    });
  } catch (error) {
    args.updateTabContent(args.tabId, {
      query: `-- Error: ${error instanceof Error ? error.message : 'Unknown error'}\n`,
      isGeneratingScript: false,
    });
  }
}

function mapObjectKindToDefinitionType(kind: ObjectKind):
  | 'view'
  | 'function'
  | 'stored-procedure'
  | null {
  switch (kind) {
    case 'view':
    case 'materialized-view':
      return 'view';
    case 'inline-tvf':
    case 'scalar-function':
      return 'function';
    case 'procedure':
      return 'stored-procedure';
    case 'trigger':
      // The shared `objectDefinitionService` doesn't surface trigger DDL —
      // a follow-up slice can extend it. Slice 24 falls back to "no script"
      // for trigger references.
      return null;
    case 'default-constraint':
    case 'check-constraint':
      // Constraints live on tables; the user can re-open the table designer
      // separately. Slice 24 doesn't open a Table Designer to avoid bringing
      // in two different navigation paths from a single click.
      return null;
  }
}

// Scheduling reveal-at-position: monaco editors aren't mounted synchronously
// when the tab is added, so we stash the desired position on a global
// scratch-pad keyed by tab id. The query-tab reveal hook (Slice 6's
// columnNavigation already established this seam) reads + clears the entry.
type RevealMap = Map<string, { line: number; column: number }>;

declare global {
  // eslint-disable-next-line no-var
  var __NAV_REFERENCES_REVEAL__: RevealMap | undefined;
}

function scheduleRevealAtPosition(tabId: string, range: SqlRange): void {
  const map = getRevealMap();
  // Convert 0-indexed SqlRange to 1-indexed Monaco line/column.
  map.set(tabId, {
    line: range.startLine + 1,
    column: range.startColumn + 1,
  });
}

function getRevealMap(): RevealMap {
  if (typeof globalThis === 'undefined') {
    return new Map();
  }
  if (!globalThis.__NAV_REFERENCES_REVEAL__) {
    globalThis.__NAV_REFERENCES_REVEAL__ = new Map();
  }
  return globalThis.__NAV_REFERENCES_REVEAL__;
}

/** Test-only — reveal queue accessor used by Slice 24 unit + E2E specs. */
export function __consumeReferenceRevealForTest(tabId: string):
  | { readonly line: number; readonly column: number }
  | null {
  const map = getRevealMap();
  const entry = map.get(tabId);
  if (!entry) return null;
  map.delete(tabId);
  return entry;
}

/** Pop the reveal request for a given tab, returning null when no request is
 *  queued. Wired into the query-editor's mount hook so the editor focuses
 *  the reference range as soon as the tab renders. */
export function consumeReferenceReveal(tabId: string):
  | { readonly line: number; readonly column: number }
  | null {
  return __consumeReferenceRevealForTest(tabId);
}
